import { HotelWithProgress, Task, TaskComment, TaskTemplate, Category, User, Feedback } from '../types';
import { logger } from '../utils/logger';
import { 
  getHotels, 
  getHotel, 
  createHotel, 
  updateHotel, 
  deleteHotel as deleteHotelFirebase,
  getHotelTasks,
  createTask as createTaskFirebase,
  updateTask as updateTaskFirebase,
  deleteTask as deleteTaskFirebase,
  addTaskComment,
  deleteTaskComment as deleteTaskCommentFirebase,
  addTaskFile as addTaskFileFirebase,
  deleteTaskFile as deleteTaskFileFirebase,
  getTemplates as getTemplatesFirebase,
  createTemplate as createTemplateFirebase,
  updateTemplate as updateTemplateFirebase,
  updateTemplateOrder as updateTemplateOrderFirebase,
  deleteTemplate as deleteTemplateFirebase,
  applyTemplatesToHotel as applyTemplatesToHotelFirebase,
  getCategories as getCategoriesFirebase,
  createCategory as createCategoryFirebase,
  updateCategory as updateCategoryFirebase,
  updateCategoryOrder as updateCategoryOrderFirebase,
  deleteCategory as deleteCategoryFirebase,
  migrateOrderFields as migrateOrderFieldsFirebase,
  getUsers as getUsersFirebase,
  getUser as getUserFirebase,
  createUser as createUserFirebase,
  updateUser as updateUserFirebase,
  deleteUser as deleteUserFirebase,
  deactivateUser as deactivateUserFirebase,
  activateUser as activateUserFirebase,
  subscribeToHotels,
  subscribeToHotel,
  subscribeToHotelTasks,
  subscribeToHotelProgress,
  subscribeToTaskComments,
  getFeedback,
  getFeedbackByHotel,
  addFeedback,
  updateFeedback,
  deleteFeedback,
  addFeedbackComment,
  subscribeToFeedback,
  subscribeToFeedbackByHotel
} from '../firebase/firestore';

class ApiService {

  // Hotels
  async getHotels(): Promise<HotelWithProgress[]> {
    try {
      return await getHotels();
    } catch (error) {
      logger.error('Error getting hotels:', error);
      throw error;
    }
  }

  async getHotel(id: string): Promise<HotelWithProgress> {
    try {
      return await getHotel(id);
    } catch (error) {
      console.error('Error getting hotel:', error);
      throw error;
    }
  }

  async createHotel(hotelData: Partial<HotelWithProgress>): Promise<HotelWithProgress> {
    try {
      return await createHotel(hotelData as any);
    } catch (error) {
      console.error('Error creating hotel:', error);
      throw error;
    }
  }

  async updateHotel(
    hotelId: string,
    hotelData: { name: string; contactPerson: string; imageUrl: string }
  ): Promise<HotelWithProgress> {
    try {
      return await updateHotel(hotelId, hotelData);
    } catch (error) {
      console.error('Error updating hotel:', error);
      throw error;
    }
  }

  async deleteHotel(hotelId: string): Promise<void> {
    try {
      await deleteHotelFirebase(hotelId);
    } catch (error) {
      console.error('Error deleting hotel:', error);
      throw error;
    }
  }

  async getHotelTasks(
    hotelId: string,
    filters?: { status?: string; category?: string; priority?: string }
  ): Promise<Task[]> {
    try {
      logger.log(`🔍 API: Getting tasks for hotel ${hotelId}`);
      const tasks = await getHotelTasks(hotelId);
              logger.log(`📋 API: Retrieved ${tasks.length} tasks for hotel ${hotelId}:`, tasks);
      
      // Apply filters client-side for now (can be optimized with Firestore queries later)
      let filteredTasks = tasks;
      
      if (filters?.status && filters.status !== 'all') {
        filteredTasks = filteredTasks.filter(task => task.status === filters.status);
      }
      
      if (filters?.category && filters.category !== 'all') {
        filteredTasks = filteredTasks.filter(task => task.category === filters.category);
      }
      
      if (filters?.priority && filters.priority !== 'all') {
        filteredTasks = filteredTasks.filter(task => task.priority === filters.priority);
      }
      
              logger.log(`📋 API: Returning ${filteredTasks.length} filtered tasks for hotel ${hotelId}`);
      return filteredTasks;
    } catch (error) {
      logger.error('Error getting hotel tasks:', error);
      throw error;
    }
  }

  // Tasks
  async updateTask(
    taskId: string,
    updates: { status?: Task['status']; assignedTo?: string }
  ): Promise<Task> {
    try {
      return await updateTaskFirebase(taskId, updates);
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  }

    async addTaskComment(
    taskId: string, 
    content: string, 
    author: string
  ): Promise<TaskComment> {
    try {
      return await addTaskComment(taskId, { 
        content, 
        author, 
        timestamp: new Date().toISOString() 
      });
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  }

  async deleteTaskComment(taskId: string, commentId: string): Promise<void> {
    try {
      await deleteTaskCommentFirebase(taskId, commentId);
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  }

  async getTask(taskId: string): Promise<Task> {
    try {
      // For now, we'll get the task from the hotel's tasks
      // This can be optimized later with a direct task query
      const hotels = await getHotels();
      for (const hotel of hotels) {
        const tasks = await getHotelTasks(hotel.id);
        const task = tasks.find(t => t.id === taskId);
        if (task) return task;
      }
      throw new Error('Task not found');
    } catch (error) {
      console.error('Error getting task:', error);
      throw error;
    }
  }

  async createTask(taskData: Partial<Task>): Promise<Task> {
    try {
      return await createTaskFirebase(taskData as any);
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  }

  async deleteTask(taskId: string): Promise<void> {
    try {
      await deleteTaskFirebase(taskId);
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  }

  // File management
  async addTaskFile(taskId: string, file: any): Promise<any> {
    try {
      return await addTaskFileFirebase(taskId, file);
    } catch (error) {
      console.error('Error adding file:', error);
      throw error;
    }
  }

  async deleteTaskFile(taskId: string, fileId: string): Promise<void> {
    try {
      await deleteTaskFileFirebase(taskId, fileId);
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  // Template Management
  async getTemplates(): Promise<TaskTemplate[]> {
    try {
      return await getTemplatesFirebase();
    } catch (error) {
      console.error('Error getting templates:', error);
      throw error;
    }
  }

  async createTemplate(templateData: Partial<TaskTemplate>): Promise<TaskTemplate> {
    try {
      return await createTemplateFirebase(templateData as any);
    } catch (error) {
      console.error('Error creating template:', error);
      throw error;
    }
  }

  async updateTemplate(templateId: string, updates: Partial<TaskTemplate>): Promise<TaskTemplate> {
    try {
      return await updateTemplateFirebase(templateId, updates);
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  }

  async updateTemplateOrder(templateId: string, newOrder: number): Promise<void> {
    try {
      await updateTemplateOrderFirebase(templateId, newOrder);
    } catch (error) {
      console.error('Error updating template order:', error);
      throw error;
    }
  }

  async deleteTemplate(templateId: string): Promise<void> {
    try {
      await deleteTemplateFirebase(templateId);
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  }

  async applyTemplatesToHotel(hotelId: string): Promise<void> {
    try {
      await applyTemplatesToHotelFirebase(hotelId);
    } catch (error) {
      console.error('Error applying templates to hotel:', error);
      throw error;
    }
  }

  // Category Management
  async getCategories(): Promise<Category[]> {
    try {
      return await getCategoriesFirebase();
    } catch (error) {
      console.error('Error getting categories:', error);
      throw error;
    }
  }

  async createCategory(categoryData: Partial<Category>): Promise<Category> {
    try {
      return await createCategoryFirebase(categoryData as any);
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  async updateCategory(categoryId: string, updates: Partial<Category>): Promise<Category> {
    try {
      return await updateCategoryFirebase(categoryId, updates);
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }

  async updateCategoryOrder(categoryId: string, newOrder: number): Promise<void> {
    try {
      await updateCategoryOrderFirebase(categoryId, newOrder);
    } catch (error) {
      console.error('Error updating category order:', error);
      throw error;
    }
  }

  async deleteCategory(categoryId: string): Promise<void> {
    try {
      await deleteCategoryFirebase(categoryId);
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }

  async migrateOrderFields(): Promise<void> {
    try {
      await migrateOrderFieldsFirebase();
    } catch (error) {
      console.error('Error migrating order fields:', error);
      throw error;
    }
  }

  // User Management
  async getUsers(): Promise<User[]> {
    try {
      return await getUsersFirebase();
    } catch (error) {
      console.error('Error getting users:', error);
      throw error;
    }
  }

  async getUser(email: string): Promise<User | null> {
    try {
      return await getUserFirebase(email);
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  }

  async createUser(userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<User> {
    try {
      return await createUserFirebase(userData);
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async updateUser(email: string, updates: Partial<User>): Promise<User> {
    try {
      return await updateUserFirebase(email, updates);
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async deleteUser(email: string): Promise<void> {
    try {
      await deleteUserFirebase(email);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  async deactivateUser(email: string): Promise<void> {
    try {
      await deactivateUserFirebase(email);
    } catch (error) {
      console.error('Error deactivating user:', error);
      throw error;
    }
  }

  async activateUser(email: string): Promise<void> {
    try {
      await activateUserFirebase(email);
    } catch (error) {
      console.error('Error activating user:', error);
      throw error;
    }
  }

  // Health check - now checks Firebase connection
  async healthCheck(): Promise<boolean> {
    try {
      // Test Firebase connection by trying to get hotels
      await getHotels();
      return true;
    } catch {
      return false;
    }
  }

  // Real-time subscription methods
  subscribeToHotels(callback: (hotels: HotelWithProgress[]) => void) {
    return subscribeToHotels(callback);
  }

  subscribeToHotel(hotelId: string, callback: (hotel: HotelWithProgress | null) => void) {
    return subscribeToHotel(hotelId, callback);
  }

  subscribeToHotelTasks(hotelId: string, callback: (tasks: Task[]) => void) {
    return subscribeToHotelTasks(hotelId, callback);
  }

  subscribeToTaskComments(taskId: string, callback: (comments: TaskComment[]) => void) {
    return subscribeToTaskComments(taskId, callback);
  }

  subscribeToHotelProgress(hotelId: string, callback: (progress: { totalTasks: number; completedTasks: number; progressPercentage: number }) => void) {
    return subscribeToHotelProgress(hotelId, callback);
  }

  // Feedback methods
  async getFeedback(): Promise<Feedback[]> {
    try {
      return await getFeedback();
    } catch (error) {
      logger.error('Error getting feedback:', error);
      throw error;
    }
  }

  async getFeedbackByHotel(hotelId: string): Promise<Feedback[]> {
    try {
      return await getFeedbackByHotel(hotelId);
    } catch (error) {
      logger.error('Error getting feedback by hotel:', error);
      throw error;
    }
  }

  async createFeedback(feedbackData: Omit<Feedback, 'id' | 'createdAt' | 'updatedAt'>): Promise<Feedback> {
    try {
      return await addFeedback(feedbackData);
    } catch (error) {
      logger.error('Error creating feedback:', error);
      throw error;
    }
  }

  async updateFeedback(feedbackId: string, updates: Partial<Feedback>): Promise<void> {
    try {
      await updateFeedback(feedbackId, updates);
    } catch (error) {
      logger.error('Error updating feedback:', error);
      throw error;
    }
  }

  async deleteFeedback(feedbackId: string): Promise<void> {
    try {
      await deleteFeedback(feedbackId);
    } catch (error) {
      logger.error('Error deleting feedback:', error);
      throw error;
    }
  }

  async addFeedbackComment(feedbackId: string, commentData: { content: string; author: string; authorEmail: string; authorName: string }): Promise<void> {
    try {
      await addFeedbackComment(feedbackId, commentData);
    } catch (error) {
      logger.error('Error adding feedback comment:', error);
      throw error;
    }
  }

  // Feedback real-time subscriptions
  subscribeToFeedback(callback: (feedbacks: Feedback[]) => void) {
    return subscribeToFeedback(callback);
  }

  subscribeToFeedbackByHotel(hotelId: string, callback: (feedbacks: Feedback[]) => void) {
    return subscribeToFeedbackByHotel(hotelId, callback);
  }
}

export const apiService = new ApiService();
